import { model as router } from '!/router'
import { api, PlaybackTriggerEnum } from '@setplex/tria-api'
import {
  createEvent,
  createStore,
  sample,
  split,
  type EventCallable,
  type StoreWritable,
} from 'effector'
import { epgCategoriesModel } from '~/entities/epg/categories'
import { playerAnalyticsModel } from '~/entities/player'
import { model as session } from '~/entities/session'
import { ctvLandingRegionFeatureModel } from '~/features/ctvLandingRegion'
import { selectEpgCategoryModel } from '~/features/epg/categories/select'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { preferencesFeatureModel } from '~/features/preferences'
import { $route } from '~/processes/navigation'
import { analyticsSharedModel } from '~/shared/analytics'
import { OPEN_FROM } from '~/shared/constants'
import { PATH } from '~/shared/constants/path'
import { model as signInModel } from '~/widgets/auth/sign-in'
import { resetEpg } from '~/widgets/epg'
import { epgPlayerWidgetModel } from '~/widgets/epg/player'
import { epgRegionsWidgetModel } from '~/widgets/epg/regions'
import { buildEpgMenuUrl, buildHomeMenuUrl } from '../lib'

export const onLogOut: EventCallable<void> = createEvent()

sample({
  clock: onLogOut,
  target: api.user.logOutFx,
})

export const $navbarRender = session.$isAuthenticated.map(
  (value) => typeof value !== 'object'
)

export const handleHomeMenuItem = createEvent()
export const handleFavoritesMenuItem = createEvent()

/**
 * The header Sign In control. It only opens the auth popup, which several other
 * surfaces do too — the event exists so that the entry point of the flow stays
 * distinguishable once it is over (OVP-3049).
 */
export const signInClicked: EventCallable<void> = createEvent()

sample({
  clock: signInClicked,
  target: signInModel.tryToOpenSignInPopup,
})

// guests get the My List page with the sign up call to action, not a registration popup
sample({
  clock: handleFavoritesMenuItem,
  fn: () => PATH.FAVORITES,
  target: router.navigatePush,
})

const $isCTVLandingPage = $route.map((route) => {
  const routeName = route?.routeName ?? ''
  return routeName === PATH.CTV
})

sample({
  clock: handleHomeMenuItem,
  source: {
    isCTVLandingPage: $isCTVLandingPage,
    ctvLandingRegion: ctvLandingRegionFeatureModel.$ctvLandingRegion,
    preferredRegion: preferencesFeatureModel.$preference,
  },
  fn: ({ isCTVLandingPage, ctvLandingRegion, preferredRegion }) =>
    buildHomeMenuUrl({
      isCTVLandingPage,
      ctvLandingRegion,
      preferredRegionAlias: preferredRegion?.alias,
    }),
  target: router.navigatePush,
})

export const handleEpgMenuItem = createEvent()

const checkEpgReset = createEvent<boolean>()

sample({
  clock: handleEpgMenuItem,
  source: {
    regionId: epgCategoriesModel.$regionId,
    epgRegion: epgRegionsWidgetModel.$selected,
  },
  fn: ({ regionId, epgRegion }) => regionId === epgRegion?.id,
  target: checkEpgReset,
})

sample({
  clock: handleEpgMenuItem,
  fn: () => PlaybackTriggerEnum.playerOpen,
  target: [
    playerAnalyticsModel.setPlaybackTrigerData,
    epgPlayerWidgetModel.resetCatchupTimeInterval,
  ],
})

split({
  source: checkEpgReset,
  match: {
    resetEpg: (value) => !value,
    resetCategoryAndChannel: (value) => value,
  },
  cases: {
    resetEpg,
    resetCategoryAndChannel: [
      selectEpgChannelModel.resetChannel,
      selectEpgCategoryModel.resetActiveId,
    ],
  },
})

sample({
  clock: handleEpgMenuItem,
  source: {
    isCTVLandingPage: $isCTVLandingPage,
    ctvLandingRegion: ctvLandingRegionFeatureModel.$ctvLandingRegion,
    ctvLandingPageSlug: ctvLandingRegionFeatureModel.$ctvLandingPageSlug,
    preferredRegion: preferencesFeatureModel.$preference,
  },
  fn: ({
    isCTVLandingPage,
    ctvLandingRegion,
    ctvLandingPageSlug,
    preferredRegion,
  }) =>
    buildEpgMenuUrl({
      isCTVLandingPage,
      ctvLandingRegion,
      ctvLandingPageSlug,
      preferredRegionAlias: preferredRegion?.alias,
    }),
  target: router.navigatePush,
})

sample({
  clock: handleEpgMenuItem,
  fn: () => `${OPEN_FROM.LIVE}/All`,
  target: analyticsSharedModel.setRouterData,
})

export const toggleMobileMenu: EventCallable<boolean> = createEvent<boolean>()

export const $isMobileMenu: StoreWritable<boolean> = createStore<boolean>(
  false
).on(toggleMobileMenu, (_, newValue) => newValue)

export const navigateToProfilePage: EventCallable<void> = createEvent()

sample({
  clock: navigateToProfilePage,
  fn: () => PATH.SETTINGS,
  target: router.navigatePush,
})
